import React from 'react';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
import { DepartmentData, PriorityTask } from '../services/dashboardApi';

ChartJS.register(ArcElement, Tooltip, Legend);

interface DepartmentDistributionChartProps {
  data: DepartmentData[];
  priorityTasks: PriorityTask[];
}

const DepartmentDistributionChart: React.FC<DepartmentDistributionChartProps> = ({ data: chartData, priorityTasks }) => {

  const data = {
    // labels: chartData.map(item => item.name),
    datasets: [
      {
        data: chartData.map(item => item.value),
        backgroundColor: chartData.map(item => item.color),
        borderWidth: 3,
        borderColor: '#fff',
        hoverBorderColor: '#fff',
        hoverBorderWidth: 4,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '65%',
    plugins: {
      legend: {
        position: 'right' as const,
        labels: {
          usePointStyle: true,
          padding: 15,
          font: {
            size: 11,
          },
          generateLabels: (chart: any) => {
            const datasets = chart.data.datasets;
            return chart.data.labels.map((label: string, i: number) => ({
              text: label,
              fillStyle: datasets[0].backgroundColor[i],
              hidden: false,
              index: i,
            }));
          },
        },
      },
      tooltip: {
        backgroundColor: '#fff',
        titleColor: '#000',
        bodyColor: '#666',
        borderColor: '#ddd',
        borderWidth: 1,
        padding: 12,
        displayColors: true,
        callbacks: {
          label: function(context: any) {
            const label = context.label || '';
            const value = context.parsed || 0;
            const total = context.dataset.data.reduce((a: number, b: number) => a + b, 0);
            const percentage = ((value / total) * 100).toFixed(1);
            return `${label}: ${percentage}%`;
          }
        }
      },
    },
  };


  const getPriorityColor = (priority: string) => {
    switch (priority.toLowerCase()) {
      case 'urgent':
        return '#F64E60';
      case 'low':
        return '#FFA800';
      case 'medium':
        return '#3699FF';
      case 'normal':
        return '#1BC5BD';
      default:
        return '#7E8299';
    }
  };

  return (
    <div className="card card-flush h-100 shadow-sm">
      <div className="card-header pt-7 pb-3">
        <h3 className="card-title align-items-start flex-column">
          <span className="card-label fw-bold text-gray-800">
            Department Distribution
          </span>
        </h3>
      </div>
      <div className="card-body pt-2 pb-2 d-flex flex-column">
        <div style={{ height: '300px', width: '100%', maxWidth: '500px' }}>
          <Doughnut data={data} options={options} />
        </div>

        <div className="table-responsive mt-2">
          <table className="table table-row-dashed gy-4 align-middle">
            <thead>
              <tr className="fw-bold text-gray-800 bg-light">
                <th className="ps-4 rounded-start">Priority</th>
                <th className="pe-4 text-end rounded-end">Tasks</th>
              </tr>
              
            </thead>
            <tbody>
              {priorityTasks.map((item, index) => (
                <tr key={index}>
                  <td className="ps-4">
                    <div className="d-flex align-items-center">
                      <div
                        className="rounded-circle me-3"
                        style={{
                          width: '10px',
                          height: '10px',
                          backgroundColor: getPriorityColor(item.priority),
                        }}
                      ></div>
                       <span className="text-gray-800 fw-semibold">{item.priority}</span>
                    </div>
                  </td>
                  <td className="pe-4 text-end">
                    <span className="badge badge-light-primary fw-bold fs-7">
                      {item.tasks}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default DepartmentDistributionChart;
